import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useCallback, useMemo } from 'react';

import { InfoCallout } from '@/components/ui/info-callout';
import { AllIntegrations } from '@/hooks/useIntegration';
import { useIntegrationSuggestions } from '@/hooks/useIntegrationSuggestions';
import { useGlobalState } from '@/state/global.state';
import { Integration } from '@/types';
import { StorageKey, useStorage } from '@/utils/storage/useStorage.util';

/**
 * Resolves an integration key (e.g. "amazon") to a human-readable name
 * (e.g. "AWS") using the AllIntegrations registry.
 */
function integrationDisplayName(key: string): string {
  const meta = AllIntegrations[key as Integration];
  return meta?.name ?? key;
}

/**
 * A dismissible banner shown once per suggested (unconfigured) integration.
 *
 * The correlation engine creates a per-integration Configuration record in
 * DynamoDB (key "integration-suggestion:<name>", value true) when a provider's
 * CanCorrelate returns true but the client hasn't configured the integration.
 * This component reads those records and displays a single banner per provider.
 *
 * Dismissals are persisted via useStorage (tenant-scoped, cross-tab synced)
 * so the banner doesn't reappear after page reloads.
 */
export function IntegrationSuggestionBanner() {
  const { suggestions } = useIntegrationSuggestions();
  const { drawerState } = useGlobalState();

  const [dismissed, setDismissed] = useStorage<string[]>(
    { key: StorageKey.DISMISSED_INTEGRATION_SUGGESTIONS },
    []
  );

  const dismissedSet = useMemo(() => new Set(dismissed), [dismissed]);

  const visible = useMemo(
    () => suggestions.filter(name => !dismissedSet.has(name)),
    [suggestions, dismissedSet]
  );

  const handleDismiss = useCallback(
    (name: string) => {
      setDismissed(prev => [...(prev ?? []), name]);
    },
    [setDismissed]
  );

  const handleConnect = useCallback(
    (name: string) => {
      const meta = AllIntegrations[name as Integration];
      if (meta) {
        drawerState.handleOpenIntegrationDrawer(meta, 'All');
      }
    },
    [drawerState]
  );

  if (visible.length === 0) return null;

  return (
    <div className="flex flex-col gap-2 px-4 pt-2">
      {visible.map(name => (
        <InfoCallout
          key={name}
          icon={<InformationCircleIcon className="size-5" />}
          onDismiss={() => handleDismiss(name)}
          dismissLabel={`Dismiss ${integrationDisplayName(name)} suggestion`}
        >
          <p>
            Some of your assets appear to be hosted on{' '}
            <span className="font-semibold">
              {integrationDisplayName(name)}
            </span>
            .{' '}
            <button
              type="button"
              className="font-medium underline hover:no-underline"
              onClick={() => handleConnect(name)}
            >
              Connect this integration
            </button>{' '}
            to enable Guard&apos;s Correlation Engine.
          </p>
        </InfoCallout>
      ))}
    </div>
  );
}
